import axios, { type AxiosRequestConfig } from 'axios';
import { refreshAccessToken } from '../lib/authRefresh';
import { tokenStorage } from '../lib/secureStorage';
import {
  AuthResponseDto,
  LoginRequest,
  RefreshRequestDto,
  UserDto,
  UserCreateDto,
  UserUpdateDto,
  ChangePasswordDto,
  ChangeRoleDto,
  SessionInfoDto,
  SystemStatistics,
  AuditLogQueryDto,
  AuditLogPagedResponseDto,
  AuditLogDto,
  AuditStatisticsDto,
} from '../types/user';
import { ItemDto, ItemCreateDto, ItemUpdateDto } from '../types/api';
import type { AdminPermissionsGrouped, AdminRoleRow } from '../types/admin';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL ?? 'http://localhost:5019/api',
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000,
});

/** Separate instance for auth endpoints to avoid interceptor loops */
const authApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL ?? 'http://localhost:5019/api',
  headers: { 'Content-Type': 'application/json' },
  timeout: 15_000,
});

export async function get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const { data } = await api.get<T>(url, config);
  return data;
}

export async function post<T>(
  url: string,
  body?: unknown,
  config?: AxiosRequestConfig
): Promise<T> {
  const { data } = await api.post<T>(url, body, config);
  return data;
}

async function authPost<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const { data } = await authApi.post<T>(url, body, config);
  return data;
}

export async function put<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const { data } = await api.put<T>(url, body, config);
  return data;
}

export async function del(url: string, config?: AxiosRequestConfig): Promise<void> {
  await api.delete(url, config);
}

api.interceptors.request.use((config) => {
  const token = tokenStorage.getAccessToken();
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error: {
    code?: string;
    config?: AxiosRequestConfig & { _retry?: boolean; headers?: Record<string, string> };
    response?: { status?: number };
  }) => {
    const originalRequest = error.config;

    if (error.code === 'ERR_NETWORK' || error.code === 'ECONNREFUSED' || !error.response) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;
      const refreshToken = tokenStorage.getRefreshToken();

      if (!refreshToken) {
        tokenStorage.clearTokens();
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }

      try {
        const response = await refreshAccessToken(refreshToken);

        if (originalRequest.headers) {
          originalRequest.headers['Authorization'] = `Bearer ${response.accessToken}`;
        }
        return await api(originalRequest);
      } catch (refreshError) {
        tokenStorage.clearTokens();
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export const authService = {
  async login(credentials: LoginRequest): Promise<AuthResponseDto> {
    return authPost<AuthResponseDto>('/auth/login', credentials);
  },

  async refresh(request: RefreshRequestDto): Promise<AuthResponseDto> {
    return authPost<AuthResponseDto>('/auth/refresh', request);
  },

  async logout(request: RefreshRequestDto): Promise<void> {
    await post<void>('/auth/logout', request);
  },

  async getMe(): Promise<UserDto> {
    return get<UserDto>('/auth/me');
  },
};

export const userService = {
  async getUsers(): Promise<UserDto[]> {
    return get<UserDto[]>('/user');
  },

  async getUser(id: number): Promise<UserDto> {
    return get<UserDto>(`/user/${id}`);
  },

  async createUser(user: UserCreateDto): Promise<UserDto> {
    return post<UserDto>('/user', user);
  },

  async updateUser(id: number, user: UserUpdateDto): Promise<UserDto> {
    return put<UserDto>(`/user/${id}`, user);
  },

  async deleteUser(id: number): Promise<void> {
    await del(`/user/${id}`);
  },

  async changePassword(id: number, data: ChangePasswordDto): Promise<void> {
    await post<void>(`/user/${id}/change-password`, data);
  },

  async changeUserRole(id: number, data: ChangeRoleDto): Promise<void> {
    await post<void>(`/user/${id}/change-role`, data);
  },
};

export const sessionService = {
  async getMySessions(): Promise<SessionInfoDto[]> {
    return get<SessionInfoDto[]>('/session/my-sessions');
  },

  async getAllSessions(): Promise<SessionInfoDto[]> {
    return get<SessionInfoDto[]>('/session/all-sessions');
  },

  async getUserSessions(userId: number): Promise<SessionInfoDto[]> {
    return get<SessionInfoDto[]>(`/session/admin/user/${userId}/sessions`);
  },

  async terminateSession(sessionId: string): Promise<void> {
    await post<void>('/session/admin/terminate', { sessionId });
  },

  async terminateAllSessions(): Promise<void> {
    await post<void>('/session/terminate-others');
  },

  async terminateUserSessions(userId: number): Promise<void> {
    await post<void>(`/session/admin/user/${userId}/terminate-all`);
  },
};

export const adminService = {
  async getRoles(): Promise<AdminRoleRow[]> {
    return get<AdminRoleRow[]>('/admin/roles');
  },

  async getPermissions(): Promise<AdminPermissionsGrouped> {
    return get<AdminPermissionsGrouped>('/admin/permissions');
  },

  async getSystemStatistics(): Promise<SystemStatistics> {
    return get<SystemStatistics>('/admin/statistics');
  },
};

export const auditService = {
  async getAuditLogs(params?: AuditLogQueryDto): Promise<AuditLogPagedResponseDto> {
    return get<AuditLogPagedResponseDto>('/audit', { params });
  },

  async getUserAuditLogs(userId: number): Promise<AuditLogDto[]> {
    return get<AuditLogDto[]>(`/audit/user/${userId}`);
  },

  async getSessionAuditLogs(sessionId: string): Promise<AuditLogDto[]> {
    return get<AuditLogDto[]>(`/audit/session/${sessionId}`);
  },

  async getAuditStatistics(): Promise<AuditStatisticsDto> {
    return get<AuditStatisticsDto>('/audit/statistics');
  },

  async getEventTypes(): Promise<string[]> {
    return get<string[]>('/audit/event-types');
  },

  async getEventCategories(): Promise<string[]> {
    return get<string[]>('/audit/event-categories');
  },
};

export const itemService = {
  async getAll(): Promise<ItemDto[]> {
    return get<ItemDto[]>('/item');
  },

  async getById(id: number): Promise<ItemDto> {
    return get<ItemDto>(`/item/${id}`);
  },

  async create(item: ItemCreateDto): Promise<ItemDto> {
    return post<ItemDto>('/item', item);
  },

  async update(id: number, item: ItemUpdateDto): Promise<ItemDto> {
    return put<ItemDto>(`/item/${id}`, item);
  },

  async delete(id: number): Promise<void> {
    await del(`/item/${id}`);
  },
};

export default api;
